import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_build-secure-hub/artifacts/9kexjmrx_indir.jfif";

const Hero = () => {
  const { t } = useI18n();

  const handleAnchor = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative overflow-hidden hero-wash hero-noise"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 md:pt-20 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        {/* Left copy */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
          className="lg:col-span-7 relative z-10"
        >
          <div
            className="inline-flex items-center gap-2 rounded-full border border-[rgba(201,162,39,0.45)] bg-white px-3 py-1.5 text-[11px] font-semibold tracking-[0.14em] uppercase text-[hsl(var(--primary))]"
            data-testid="hero-badge"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-[hsl(var(--accent))]" />
            {t("hero.eyebrow")}
          </div>

          <h1
            className="mt-6 font-display text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.05] tracking-tight text-[hsl(var(--primary))]"
            data-testid="hero-headline"
          >
            {t("hero.headline")}
          </h1>

          <p className="mt-5 max-w-2xl text-base md:text-lg leading-relaxed text-slate-700">
            {t("hero.subhead")}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <Button
              asChild
              size="lg"
              data-testid="hero-primary-cta"
              className="h-12 px-6 rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--tbs-navy-800))] text-white shadow-[0_10px_30px_rgba(15,23,42,0.18)]"
            >
              <a href="#contact" onClick={(e) => handleAnchor(e, "contact")}>
                {t("hero.primaryCta")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              data-testid="hero-secondary-cta"
              className="h-12 px-6 rounded-xl bg-white text-[hsl(var(--primary))] border-[rgba(201,162,39,0.55)] hover:bg-[rgba(243,231,194,0.35)]"
            >
              <a href="#services" onClick={(e) => handleAnchor(e, "services")}>
                {t("hero.secondaryCta")}
              </a>
            </Button>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))]" />
              {t("hero.badge")}
            </div>
          </div>
        </motion.div>

        {/* Right image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="relative">
            <div className="absolute -inset-4 -z-10 rounded-[28px] bg-[hsl(var(--accent))]/10 blur-2xl" />
            <div className="relative overflow-hidden rounded-3xl border border-[rgba(201,162,39,0.3)] bg-[hsl(var(--tbs-navy-950))] shadow-[0_30px_80px_rgba(11,18,32,0.18)] aspect-square mx-auto w-full max-w-[560px]">
              <img
                src={HERO_IMG}
                alt="Istanbul · Bosphorus Bridge connecting Europe & Asia with mosque silhouettes at sunset"
                className="absolute inset-0 w-full h-full object-contain"
                style={{
                  imageRendering: "auto",
                  filter: "contrast(1.06) saturate(1.08) brightness(1.02)",
                }}
                loading="eager"
                decoding="sync"
              />
              {/* Crescent moon overlay (gold accent, top-left) */}
              <svg
                className="absolute top-4 left-4 h-9 w-9 drop-shadow-[0_4px_18px_rgba(212,162,76,0.6)] z-10"
                viewBox="0 0 64 64"
                aria-hidden="true"
              >
                <defs>
                  <radialGradient id="moonGlow" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#FFE9A8" />
                    <stop offset="60%" stopColor="#D4A24C" />
                    <stop offset="100%" stopColor="#9A7521" />
                  </radialGradient>
                </defs>
                <path
                  d="M44 12a20 20 0 1 0 0 40 14 14 0 1 1 0-40z"
                  fill="url(#moonGlow)"
                />
              </svg>
            </div>
            {/* floating stat card */}
            <div className="absolute -bottom-6 -left-4 md:-left-10 bg-white rounded-2xl border border-slate-200 shadow-[0_18px_50px_rgba(15,23,42,0.12)] px-5 py-4 w-56">
              <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500">TBS</div>
              <div className="mt-1 font-display text-2xl text-[hsl(var(--primary))]">12+ Years</div>
              <div className="text-xs text-slate-600 mt-0.5">of corporate consultancy</div>
              <div className="gold-rule mt-3" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
